import { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, FileUp, Plus } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { EditorModal } from "@/components/EditorModal";
import { ClientCsvImportPanel } from "@/components/RequestClientTools";
import { StaffingRequestForm } from "@/components/StaffingRequestForm";
import { BoardBulkBar } from "@/components/board/BoardBulkBar";
import { BoardConfirm } from "@/components/board/BoardConfirm";
import { BoardFilterChips } from "@/components/board/BoardFilterChips";
import { BoardMetricCards } from "@/components/board/BoardMetricCards";
import { BoardTable } from "@/components/board/BoardTable";
import { RequestDrawer } from "@/components/board/RequestDrawer";
import { exportRowsCsv, isInProgress, statusKey } from "@/components/board/boardFormat";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const blankFilters = { client_id: "", role: "", priority: "", status: "", recruiter_id: "", region: "" };
const emptyMeta = { clients: [], roles: [], recruiters: [], regions: [] };
const DELETE_ROLES = ["system_administrator", "operations", "staffing_coordinator"];
const ACTION_ENDPOINT = { archive: "/staffing-board/archive", restore: "/staffing-board/restore", purge: "/staffing-board/purge" };
const ACTION_MESSAGE = {
  archive: (n) => `${n} request${n === 1 ? "" : "s"} moved to Archived. Restore or permanently delete from the Archived view.`,
  restore: (n) => `${n} request${n === 1 ? "" : "s"} restored to the board.`,
  purge: (n) => `${n} request${n === 1 ? "" : "s"} permanently deleted.`,
};

export default function MasterStaffingBoardPage() {
  const { user } = useAuth();
  const canManage = ["system_administrator", "operations", "staffing_coordinator"].includes(user?.role);
  const canDelete = DELETE_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [archivedRows, setArchivedRows] = useState([]);
  const [view, setView] = useState("active");
  const [meta, setMeta] = useState(emptyMeta);
  const [people, setPeople] = useState({ candidates: [], providers: [] });
  const [filters, setFilters] = useState(blankFilters);
  const [cardFilter, setCardFilter] = useState(() => {
    const card = new URLSearchParams(window.location.search).get("card");
    return card === "attention" ? "attention" : "";
  });
  const [selected, setSelected] = useState(() => new Set());
  const [drawer, setDrawer] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get("request");
    return requested ? { id: requested, focus: "assign" } : null;
  });
  const [modal, setModal] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [archiveReason, setArchiveReason] = useState("Archived from Master Staffing Board");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const loadBoard = useCallback(async (activeFilters) => {
    try {
      setError("");
      const params = Object.fromEntries(Object.entries(activeFilters).filter(([, value]) => value));
      const response = await api.get("/staffing-board", { params });
      setRows(response.data);
      setSelected(new Set());
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  const loadArchived = useCallback(async () => {
    if (!canDelete) return;
    try {
      const response = await api.get("/staffing-board/archived");
      setArchivedRows(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [canDelete]);

  useEffect(() => {
    async function loadInitialData() {
      try {
        const metaResponse = await api.get("/staffing-board/meta");
        setMeta(metaResponse.data);
        if (canManage) {
          const [candidates, providers] = await Promise.all([api.get("/candidates"), api.get("/providers/approved")]);
          setPeople({ candidates: candidates.data, providers: providers.data });
        }
      } catch (requestError) {
        setError(formatApiError(requestError));
      }
      await Promise.all([loadBoard(blankFilters), loadArchived()]);
    }
    loadInitialData();
  }, [loadBoard, loadArchived, canManage]);

  const counts = useMemo(
    () => ({
      Open: rows.filter((row) => statusKey(row.status) === "open").length,
      "In progress": rows.filter(isInProgress).length,
      Filled: rows.filter((row) => statusKey(row.status) === "filled").length,
      attention: rows.filter((row) => row.attention_reasons.length > 0).length,
    }),
    [rows],
  );

  const visibleRows = useMemo(() => {
    if (view === "archived") return archivedRows;
    if (!cardFilter) return rows;
    if (cardFilter === "attention") return rows.filter((row) => row.attention_reasons.length > 0);
    if (cardFilter === "In progress") return rows.filter(isInProgress);
    return rows.filter((row) => statusKey(row.status) === cardFilter.toLowerCase());
  }, [rows, archivedRows, cardFilter, view]);

  function switchView(nextView) {
    setView(nextView);
    setSelected(new Set());
    setMessage("");
  }

  function updateFilter(event) {
    const next = { ...filters, [event.target.name]: event.target.value };
    setFilters(next);
    loadBoard(next);
  }

  function toggleRow(requestId) {
    setSelected((current) => {
      const next = new Set(current);
      next.has(requestId) ? next.delete(requestId) : next.add(requestId);
      return next;
    });
  }

  function toggleAll() {
    const allSelected = visibleRows.length > 0 && visibleRows.every((row) => selected.has(row.id));
    setSelected(allSelected ? new Set() : new Set(visibleRows.map((row) => row.id)));
  }

  async function applyBulk(payload, ids = [...selected]) {
    if (!ids.length) return;
    setIsUpdating(true);
    setError("");
    try {
      const response = await api.post("/staffing-board/bulk-update", { request_ids: ids, ...payload });
      setMessage(`${response.data.updated_count} request${response.data.updated_count === 1 ? "" : "s"} updated.`);
      await loadBoard(filters);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsUpdating(false);
    }
  }

  async function runLifecycle() {
    if (!confirm) return;
    setIsUpdating(true);
    setError("");
    try {
      await api.post(ACTION_ENDPOINT[confirm.action], confirm.action === "archive" ? { request_ids: confirm.ids, reason: archiveReason } : { request_ids: confirm.ids });
      setMessage(ACTION_MESSAGE[confirm.action](confirm.ids.length));
      setConfirm(null);
      if (drawer && confirm.ids.includes(drawer.id)) setDrawer(null);
      await Promise.all([loadBoard(filters), loadArchived()]);
      setSelected(new Set());
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsUpdating(false);
    }
  }

  function handleRowAction(row, action, payload) {
    if (action === "copy") {
      navigator.clipboard?.writeText(row.id);
      setMessage(`${row.id} copied.`);
      return;
    }
    if (action === "priority") {
      applyBulk({ priority: payload }, [row.id]);
      return;
    }
    if (action === "archive" || action === "restore" || action === "purge") {
      setConfirm({ action, ids: [row.id] });
      return;
    }
    setDrawer({ id: row.id, focus: action === "view" ? "" : action });
  }

  const selectedRows = rows.filter((row) => selected.has(row.id));
  const archivedView = view === "archived";

  return (
    <div className="app-shell" data-testid="master-staffing-board-shell">
      <AppSidebar activeItem="Master Staffing Board" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page board-page" data-testid="master-staffing-board-page">
          <div className="page-intro">
            <div>
              <p className="eyebrow">OPERATIONAL COMMAND CENTER</p>
              <h1>Master Staffing Board</h1>
              <p className="page-subtitle">Every staffing request, its owner, and its status in one working view.</p>
            </div>
            {canManage && (
              <div className="board-actions">
                <button className="button button-secondary" data-testid="import-client-csv-button" type="button" onClick={() => setModal("import")}>
                  <FileUp size={15} /> Import client CSV
                </button>
                <button className="button button-primary" data-testid="new-request-button" type="button" onClick={() => setModal("new")}>
                  <Plus size={16} /> New request
                </button>
              </div>
            )}
          </div>

          {error && <p className="form-message error-message" data-testid="staffing-board-error">{error}</p>}
          {message && <p className="form-message success-message" data-testid="staffing-board-message">{message}</p>}

          {canDelete && (
            <div className="board-view-toggle" data-testid="board-view-toggle" role="tablist">
              <button aria-selected={!archivedView} className={!archivedView ? "is-active" : ""} data-testid="board-view-active" role="tab" type="button" onClick={() => switchView("active")}>
                Active requests <strong>{rows.length}</strong>
              </button>
              <button aria-selected={archivedView} className={archivedView ? "is-active" : ""} data-testid="board-view-archived" role="tab" type="button" onClick={() => switchView("archived")}>
                <Archive size={13} /> Archived <strong data-testid="board-archived-count">{archivedRows.length}</strong>
              </button>
            </div>
          )}

          {!archivedView && (
            <>
              <BoardMetricCards active={cardFilter} counts={counts} onSelect={setCardFilter} />
              <BoardFilterChips filters={filters} meta={meta} onChange={updateFilter} onClear={() => { setFilters(blankFilters); loadBoard(blankFilters); }} />
            </>
          )}
          <p className="board-count" data-testid="staffing-board-summary">
            {archivedView ? (
              <><strong>{archivedRows.length}</strong> archived request{archivedRows.length === 1 ? "" : "s"} · restore or permanently delete</>
            ) : (
              <><strong>{visibleRows.length}</strong> of {rows.length} requests{cardFilter ? ` · ${cardFilter === "attention" ? "Needs attention" : cardFilter}` : ""}</>
            )}
          </p>
          {canManage && selected.size > 0 && (
            <BoardBulkBar
              archived={archivedView}
              canDelete={canDelete}
              count={selected.size}
              isUpdating={isUpdating}
              meta={meta}
              onApply={(payload) => applyBulk(payload)}
              onClear={() => setSelected(new Set())}
              onDelete={() => setConfirm({ action: "archive", ids: [...selected] })}
              onExport={() => exportRowsCsv(selectedRows)}
              onPurge={() => setConfirm({ action: "purge", ids: [...selected] })}
              onRestore={() => setConfirm({ action: "restore", ids: [...selected] })}
            />
          )}
          <BoardTable
            archived={archivedView}
            canDelete={canDelete}
            canManage={canManage}
            rows={visibleRows}
            selected={selected}
            onAction={handleRowAction}
            onOpen={(row) => setDrawer({ id: row.id, focus: "" })}
            onToggle={toggleRow}
            onToggleAll={toggleAll}
          />

          {drawer && (
            <RequestDrawer
              canManage={canManage}
              candidates={people.candidates}
              focus={drawer.focus}
              meta={meta}
              providers={people.providers}
              requestId={drawer.id}
              onChanged={() => loadBoard(filters)}
              onClose={() => setDrawer(null)}
            />
          )}
          {confirm && (
            <EditorModal onClose={() => setConfirm(null)} testId="board-confirm-modal">
              <BoardConfirm action={confirm.action} ids={confirm.ids} isWorking={isUpdating} reason={archiveReason} reasons={meta.archive_reasons || []} onCancel={() => setConfirm(null)} onConfirm={runLifecycle} onReasonChange={setArchiveReason} />
            </EditorModal>
          )}
          {modal === "new" && (
            <EditorModal onClose={() => setModal("")} testId="new-request-modal">
              <StaffingRequestForm clientOptions={meta.clients} onCancel={() => setModal("")} onCreated={(request) => { setModal(""); setMessage(`${request.id} created for ${request.client_name}.`); loadBoard(filters); }} />
            </EditorModal>
          )}
          {modal === "import" && (
            <EditorModal onClose={() => setModal("")} testId="client-csv-import-modal">
              <ClientCsvImportPanel onApplied={() => loadBoard(filters)} onClose={() => setModal("")} />
            </EditorModal>
          )}
        </main>
      </div>
    </div>
  );
}
